import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PersonalDataForm from '@containers/Auth/components/personal-data-form';
import { useDispatch, useSelector } from 'react-redux';
import { AuthActions } from '@redux';
import { AuthenticationDialog } from '@single-platform/components';
import { allLoginStatus, history } from '@config';
import { Logout } from '@utilities';
import { OtpForm } from '@containers/Auth/methods/otp/otpForm';
import { UnauthorizedCompanyModal } from '@components/Signing/UnauthorizedCompanyModal';
import { storageHandler } from '@containers/SSOLoginContainer/storageHandler';

export const Biometrics = ({ personalData, setSubmitStatus, setCurrentStep }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isWaitingForConfirmation, setIsWaitingForConfirmation] = useState(false);
  const [requiresOtp, setRequiresOtp] = useState(false);
  const authState = useSelector((state) => state.AuthReducers);
  const [isUnauthorizedCompany, setIsUnauthorizedCompany] = useState(false);
  const [personalId, setPersonalId] = useState('');
  const submit = (data) => {
    setPersonalId(data.customerId);
    setIsWaitingForConfirmation(true);
  };

  const initiateLogin = async () => {
    const data = {
      personalCode: personalId,
      email: personalData.email,
      country: personalData.country
    };

    if (!data.personalCode) return;
    const newWindow = window.open('', '_blank');

    try {
      const response = await dispatch(AuthActions.biometricsInit(data));
      if (response.payload.redirectUrl && newWindow) {
        newWindow.location.href = response.payload.redirectUrl;
      } else if (newWindow) {
        newWindow.close();
      }

      setSubmitStatus({
        isSubmitting: true,
        type: 'biometrics'
      });

      return response.payload;
    } catch (error) {
      newWindow.close();
      setSubmitStatus({
        isSubmitting: false,
        type: null
      });
      setIsWaitingForConfirmation(false);
      setCurrentStep('loginMethods');
      return { state: 'failed' };
    }
  };

  const completeLogin = async ({ sessionId, token }) => {
    if (!sessionId) {
      return { state: 'failed' };
    }
    sessionStorage.setItem('loginToken', token);
    sessionStorage.setItem('loginSessionId', sessionId);
    const response = await dispatch(AuthActions.authorizationStatus(token));
    return response.payload;
  };

  const onBackHandle = () => {
    authState.isWaitingForConfirmation = false;
    setIsWaitingForConfirmation(false);
    setSubmitStatus({
      isSubmitting: false,
      type: null
    });
    setCurrentStep('loginMethods');
  };

  const onSuccessHandle = async (data) => {
    if (data.emailOtpRequired) {
      setRequiresOtp(true);
    } else {
      const token = {
        refreshToken: data.refreshToken,
        accessToken: data.token,
        expiresIn: data.expiresIn
      };
      storageHandler.setSessionStorage('token', JSON.stringify(token));
      storageHandler.setSessionStorage('authType', 'biometrics');
      await authorizedCompanies();
    }
  };

  const authorizedCompanies = async () => {
    const result = await dispatch(AuthActions.getAuthorizedCompanies());
    if (result) {
      switch (result.payload.status) {
        case allLoginStatus.noAuthorizeCompany:
          setIsUnauthorizedCompany(true);
          break;
        case allLoginStatus.selectCompany:
          history.push('/registration');
          break;
        default:
          break;
      }
    } else onFailHandle();
  };

  const onFailHandle = async () => {
    sessionStorage.clear();
    setIsUnauthorizedCompany(false);
    await Logout();
    setRequiresOtp(false);
    setIsWaitingForConfirmation(false);
    authState.isWaitingForConfirmation = false;
    setSubmitStatus({
      isSubmitting: false,
      type: null
    });
  };

  const pollingConfiguration = {
    init: initiateLogin,
    complete: completeLogin
  };

  useEffect(() => {
    setPersonalId('');
    if (authState.isWaitingForConfirmation !== undefined) {
      setIsWaitingForConfirmation(authState.isWaitingForConfirmation);
    }
  }, [authState.isWaitingForConfirmation]);

  return (
    <>
      {!requiresOtp ? (
        <>
          <AuthenticationDialog
            testId="Biometrics"
            isWaitingForConfirmation={isWaitingForConfirmation}
            onBackHandle={onBackHandle}
            onSuccessHandle={onSuccessHandle}
            onFailHandle={onFailHandle}
            backLinkLabel={t('back')}
            verificationLabel={t('biometrics_Info_Message')}
            pollingConfig={pollingConfiguration}
          >
            <PersonalDataForm
              displayPhoneInput={false}
              onSubmit={submit}
              isBiometric={true}
              email={personalData.email}
            />
          </AuthenticationDialog>
        </>
      ) : (
        <OtpForm otpRequest={authorizedCompanies}></OtpForm>
      )}
      {isUnauthorizedCompany && <UnauthorizedCompanyModal onCancel={onFailHandle} />}
    </>
  );
};
